'use client'

import * as React from 'react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

const LOGFRAME_COLLECTION = 'projectLogframes'
const AUDIT_COLLECTION = 'auditLogs'

export default function ProjectMonitoringPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [logframes, setLogframes] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [open, setOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState({
    level: '',
    statement: '',
    indicators: '',
    baseline: '',
    target: '',
    meansOfVerification: '',
    assumptions: '',
    sdgCompliance: {},
    strategicPillarCompliance: {},
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadLogframes() {
      try {
        const logframeQuery = query(
          collection(db, LOGFRAME_COLLECTION),
          where('projectId', '==', project.id),
        )
        const snapshot = await getDocs(logframeQuery)
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setLogframes(records)
      } catch {
        setLogframes([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadLogframes()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const updateForm = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const updateCompliance = (group, key, value) => {
    setForm((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: value,
      },
    }))
  }

  const resetForm = () => {
    setForm({
      level: '',
      statement: '',
      indicators: '',
      baseline: '',
      target: '',
      meansOfVerification: '',
      assumptions: '',
      sdgCompliance: {},
      strategicPillarCompliance: {},
    })
  }

  const hasIncompleteCompliance = (items, map = {}) =>
    items.some((item) => map[item] !== 'yes' && map[item] !== 'no')

  const summarizeCompliance = (map = {}) => {
    const values = Object.values(map)
    if (values.length === 0) return '—'
    const yesCount = values.filter((value) => value === 'yes').length
    const noCount = values.filter((value) => value === 'no').length
    if (yesCount === 0 && noCount === 0) return '—'
    return `Yes ${yesCount} / No ${noCount}`
  }

  const handleSave = async () => {
    if (!form.level.trim() || !form.statement.trim() || !form.indicators.trim()) {
      toast.error('Please fill in the level, statement, and indicators.')
      return
    }
    if (
      (project.sdgMappings.length > 0 &&
        hasIncompleteCompliance(project.sdgMappings, form.sdgCompliance)) ||
      (project.strategicPillars.length > 0 &&
        hasIncompleteCompliance(project.strategicPillars, form.strategicPillarCompliance))
    ) {
      toast.error('Please mark compliance for all SDGs and strategic pillars.')
      return
    }
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        projectId: project.id,
        level: form.level.trim(),
        statement: form.statement.trim(),
        indicators: form.indicators.trim(),
        baseline: form.baseline.trim(),
        target: form.target.trim(),
        meansOfVerification: form.meansOfVerification.trim(),
        assumptions: form.assumptions.trim(),
        sdgCompliance: form.sdgCompliance,
        strategicPillarCompliance: form.strategicPillarCompliance,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, LOGFRAME_COLLECTION), payload)
      setLogframes((prev) => [{ id: docRef.id, ...payload }, ...prev])
      try {
        const currentUser = auth.currentUser
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: currentUser?.uid || 'system',
          userDisplayName: currentUser?.displayName || currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: project.id,
          changes: {
            before: '',
            after: `Added M&E logframe entry (${payload.level})`,
          },
        })
      } catch (error) {
        console.warn('Failed to write audit log', error)
      }
      toast.success('M&E entry added.')
      resetForm()
      setOpen(false)
    } catch (error) {
      toast.error('Failed to save M&E entry', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monitoring & Evaluation</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Set indicators and track progress against targets for this project.
          </p>
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">SDG Alignment</p>
            {project.sdgMappings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No SDG goals selected.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {project.sdgMappings.map((goal) => (
                  <Badge key={goal} variant="secondary" className="text-[10px]">
                    SDG {goal}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">Strategic Pillars</p>
            {project.strategicPillars.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pillars assigned.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {project.strategicPillars.map((pillar) => (
                  <Badge key={pillar} variant="secondary" className="text-[10px]">
                    {pillar}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">M&E Logframe</CardTitle>
        {canEdit ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm">Add M&E Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add M&E Entry</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Level</Label>
                  <Input
                    value={form.level}
                    onChange={updateForm('level')}
                    placeholder="Goal, Outcome, Output, Activity"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Statement</Label>
                  <Textarea
                    rows={3}
                    value={form.statement}
                    onChange={updateForm('statement')}
                    placeholder="Statement of intent"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Indicators</Label>
                  <Textarea
                    rows={2}
                    value={form.indicators}
                    onChange={updateForm('indicators')}
                    placeholder="Indicator(s) to measure progress"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Baseline</Label>
                    <Input
                      value={form.baseline}
                      onChange={updateForm('baseline')}
                      placeholder="Baseline value"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target</Label>
                    <Input
                      value={form.target}
                      onChange={updateForm('target')}
                      placeholder="Target value"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Means of Verification</Label>
                  <Textarea
                    rows={2}
                    value={form.meansOfVerification}
                    onChange={updateForm('meansOfVerification')}
                    placeholder="Data sources, reports, surveys"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assumptions</Label>
                  <Textarea
                    rows={2}
                    value={form.assumptions}
                    onChange={updateForm('assumptions')}
                    placeholder="Key assumptions or risks"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SDG Compliance</Label>
                  {project.sdgMappings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No SDG goals selected.</p>
                  ) : (
                    <div className="space-y-2">
                      {project.sdgMappings.map((goal) => (
                        <div
                          key={goal}
                          className="flex flex-col gap-2 rounded-lg border px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <span className="text-sm font-medium text-foreground">SDG {goal}</span>
                          <Select
                            value={form.sdgCompliance[goal] || ''}
                            onValueChange={(value) => updateCompliance('sdgCompliance', goal, value)}
                          >
                            <SelectTrigger className="w-full sm:w-32">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Strategic Pillar Compliance</Label>
                  {project.strategicPillars.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No pillars assigned.</p>
                  ) : (
                    <div className="space-y-2">
                      {project.strategicPillars.map((pillar) => (
                        <div
                          key={pillar}
                          className="flex flex-col gap-2 rounded-lg border px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <span className="text-sm font-medium text-foreground">{pillar}</span>
                          <Select
                            value={form.strategicPillarCompliance[pillar] || ''}
                            onValueChange={(value) =>
                              updateCompliance('strategicPillarCompliance', pillar, value)
                            }
                          >
                            <SelectTrigger className="w-full sm:w-32">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button className="w-full" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Entry'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        ) : null}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Level</TableHead>
                  <TableHead>Statement</TableHead>
                  <TableHead>Indicators</TableHead>
                  <TableHead>Baseline</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Means of Verification</TableHead>
                  <TableHead>Assumptions</TableHead>
                  <TableHead>SDG Compliance</TableHead>
                  <TableHead>Strategic Pillars</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
                      Loading M&E entries...
                    </TableCell>
                  </TableRow>
                ) : logframes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
                      No M&E entries added yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  logframes.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">{entry.level || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">
                        {entry.statement || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {entry.indicators || '—'}
                      </TableCell>
                      <TableCell>{entry.baseline || '—'}</TableCell>
                      <TableCell>{entry.target || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {entry.meansOfVerification || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {entry.assumptions || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {summarizeCompliance(entry.sdgCompliance)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {summarizeCompliance(entry.strategicPillarCompliance)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
